import { useMemo, useState } from "react";
import Button from "@mui/material/Button";
import {
  AutoFilter,
  DataListView,
  DataTable,
  Filter,
} from "@/shared/components";
import { ErrorMessage } from "@/shared/components";
import type { DataListViewColumns } from "@/shared/components/DataListView";
import type { Schema } from "../Form/types";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { useQuery } from "@apollo/client";
import type { MutationFunctionOptions } from "@apollo/client";
import { useSnackbar } from "notistack";
import { LocalStorage } from "@/shared/utils/localstorage";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { QuickFilter } from "./QuickFilter";

function getIds<Row extends { id: string }>(
  nodes: Row[],
  selectedRows: number[]
): string[] {
  return nodes
    .filter((_, index) => selectedRows.includes(index))
    .map(({ id }) => id);
}

const ls = LocalStorage("admin-crud");

export interface InnerCrudListProps<Row extends { id: string } = any> {
  filterSchema?: any;
  filterQuery?: any;
  filterQueryVariables?: any;
  autoFilter?: boolean; // Автоматические фильтры без кнопки "Применить"
  listQuery: any;
  listPollInterval?: number;
  listQueryVariables?: any;
  texts?: Record<string, any>;
  getTableData: (data: any) => { nodes: Row[]; count: number };
  mapColumns: (row: Row) => any[];
  columns: any[];
  download?: boolean;
  createButton?: boolean;
  deleteMutation?: any;
  deleteMutationVariables?: any;
  restoreMutation?: any;
  repeatMutation?: any;
  repeatMutationVariables?: any;
  onClickCreate?: () => void;
  /** Показывать список карточками вместо таблицы (нужны mobileColumns) */
  useMobileView?: boolean;
  mobileColumns?: DataListViewColumns<Row>;
  /** Какие строки можно выбирать; у остальных чекбокс скрыт */
  hasShowSelect?: (row: Row) => boolean;
  /** Полностью заменяет набор кнопок над выбранными строками */
  selectableComponent?:
    | React.ReactNode
    | ((
        selectedIds: string[],
        onClearSelection?: () => void
      ) => React.ReactNode);
}

function getFilterFieldNames(schema: any): string[] {
  if (!schema) return [];
  if (schema.tabs) {
    // Берём первый таб (или можно доработать для активного таба)
    const firstTabKey = Object.keys(schema.tabs)[0];
    if (firstTabKey && schema.tabs[firstTabKey]?.schema) {
      return Object.keys(schema.tabs[firstTabKey].schema);
    }
    return [];
  }
  return Object.keys(schema);
}

export function InnerCrudList<Row extends { id: string } = any>({
  filterSchema,
  filterQuery,
  filterQueryVariables,
  autoFilter,

  listQuery,
  listPollInterval,
  listQueryVariables,

  texts = {},

  getTableData,
  mapColumns,
  columns,

  download,

  createButton,

  deleteMutation,
  deleteMutationVariables,

  restoreMutation,

  repeatMutation,
  repeatMutationVariables,

  onClickCreate,

  useMobileView,
  mobileColumns,
  hasShowSelect,
  selectableComponent,
}: InnerCrudListProps<Row>) {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const showMobileView = !!useMobileView && !!mobileColumns && isMobile;

  // Схема делится ровно как в CrudList: quick-поля уходят в чипы, остальные — в форму
  const filterSchemas = useMemo(() => {
    const result: { filter: Schema; quickFilter: Schema } = {
      filter: {},
      quickFilter: {},
    };

    if (filterSchema) {
      Object.entries(filterSchema).forEach(([key, field]) => {
        if ((field as any)?.priority === "quick") {
          result.quickFilter[key] = field as any;
        } else {
          result.filter[key] = field as any;
        }
      });
    }

    return result;
  }, [filterSchema]);

  const [filter, setFilter] = useState({ ...listQueryVariables?.filter });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    ls.getItemOrDefault("rowsPerPage", 10)
  );

  const variables = {
    ...listQueryVariables,
    filter: Object.keys(filter).length > 0 ? filter : undefined,
    page,
    rowsPerPage,
  };

  const refetchQueries = [{ query: listQuery, variables }];

  const listQueryResult = useQuery(listQuery, {
    fetchPolicy: "cache-and-network",
    pollInterval: listPollInterval,
    variables,
  });

  const pickQuickFilterValues = (source: Record<string, unknown>) => {
    const quickKeys = Object.keys(filterSchemas.quickFilter);

    return Object.fromEntries(
      Object.entries(source).filter(([key]) => quickKeys.includes(key))
    );
  };

  const onFilterSubmit = (newFilter: any) => {
    setPage(0);
    // Фильтруем null значения и пустые массивы
    const cleanFilter = Object.fromEntries(
      Object.entries(newFilter).filter(([, value]) => {
        if (value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (value === "") return false;
        return true;
      })
    );

    setFilter({
      ...pickQuickFilterValues(filter),
      ...cleanFilter,
      ...listQueryVariables?.filter,
    });
  };

  const onQuickFilterChange = (newFilter: Record<string, unknown>) => {
    setPage(0);

    const nonQuickKeys = Object.keys(filterSchemas.filter);
    const nonQuickFilter = Object.fromEntries(
      Object.entries(filter).filter(([key]) => nonQuickKeys.includes(key))
    );

    setFilter({
      ...nonQuickFilter,
      ...newFilter,
      ...listQueryVariables?.filter,
    });
  };

  const createMutationRunner = (
    mutationDoc?: any,
    baseVariables?: any
  ):
    | ((options?: MutationFunctionOptions<any, { ids: string[] }>) => void)
    | undefined => {
    if (!mutationDoc) return undefined;

    return (options) => {
      const {
        variables: optionVariables,
        refetchQueries: optionRefetch,
        ...restOptions
      } = options || {};

      const resolvedBaseVariables =
        typeof baseVariables === "function"
          ? baseVariables()
          : (baseVariables ?? {});

      listQueryResult.client.mutate({
        mutation: mutationDoc,
        refetchQueries: optionRefetch ?? refetchQueries,
        variables: {
          ...resolvedBaseVariables,
          ...optionVariables,
        } as { ids: string[] },
        ...restOptions,
      });
    };
  };

  const deleteItemsRunner = createMutationRunner(
    deleteMutation,
    deleteMutationVariables
  );
  const restoreItemsRunner = createMutationRunner(restoreMutation);

  const onChangePage = (page: number) => {
    setPage(page);
  };

  const onChangeRowsPerPage = (rowsPerPage: number) => {
    setPage(0);
    setRowsPerPage(rowsPerPage);
    ls.setItem("rowsPerPage", rowsPerPage);
  };

  const { nodes = [], count = 0 } = getTableData(listQueryResult.data) || {};
  const tableData = nodes.map(mapColumns);

  const selectableRows =
    !!deleteMutation || !!repeatMutation ? "multiple" : "none";

  const _createButton = createButton
    ? {
        text: texts.createItem,
        onClick: onClickCreate,
      }
    : undefined;

  const deleteButton = deleteMutation && {
    text: texts.deleteItems,
    onClick(selectedRows: number[]) {
      const ids = getIds(nodes, selectedRows);

      const onClickRestore = () => {
        listQueryResult.client
          .mutate({
            mutation: restoreMutation,
            refetchQueries,
            variables: {
              ids,
            },
          })
          .then(() => {
            return enqueueSnackbar(texts.itemsRestored, {
              variant: "success",
            });
          })
          .catch((error) => {
            enqueueSnackbar(error.message, {
              variant: "error",
            });
          });
      };

      listQueryResult.client
        .mutate({
          mutation: deleteMutation,
          refetchQueries,
          variables: {
            ...deleteMutationVariables,
            ids,
          },
        })
        .then(() => {
          if (restoreMutation) {
            enqueueSnackbar(texts.itemsDeleted, {
              variant: "success",
              action: (
                <Button size="small" color="inherit" onClick={onClickRestore}>
                  {texts.cancel}
                </Button>
              ),
            });
          } else {
            enqueueSnackbar(texts.itemsDeleted, {
              variant: "success",
            });
          }
          return;
        })
        .catch((error) => {
          enqueueSnackbar(error.message, {
            variant: "error",
          });
        });
    },
  };

  const repeatButton = repeatMutation && {
    text: texts.repeatItems,
    onClick(selectedRows: number[]) {
      const ids = getIds(nodes, selectedRows);
      listQueryResult.client
        .mutate({
          mutation: repeatMutation,
          refetchQueries,
          variables: {
            ...repeatMutationVariables,
            ids,
          },
        })
        .then(() => {
          return enqueueSnackbar(texts.itemsRepeated, {
            variant: "success",
          });
        })
        .catch((error) => {
          enqueueSnackbar(error.message, {
            variant: "error",
          });
        });
    },
  };

  const refetchButton = {
    text: texts.refetchList,
    onClick: () => listQueryResult.refetch(),
  };

  const filterFieldNames = getFilterFieldNames(filterSchemas.filter);
  const filteredInitialValues = filterFieldNames.length
    ? Object.fromEntries(
        Object.entries(filter).filter(([key]) => filterFieldNames.includes(key))
      )
    : {};

  return (
    <Stack spacing={1} sx={{ pb: 1 }}>
      {listQueryResult.error && <ErrorMessage error={listQueryResult.error} />}

      {Object.keys(filterSchemas.filter).length > 0 ? (
        <Card>
          <CardContent>
            {autoFilter ? (
              <AutoFilter
                query={filterQuery}
                variables={filterQueryVariables}
                onSubmit={onFilterSubmit}
                initialValues={filteredInitialValues}
                schema={filterSchemas.filter}
                title={texts.listTitle}
              />
            ) : (
              <Filter
                query={filterQuery}
                variables={filterQueryVariables}
                onSubmit={onFilterSubmit}
                initialValues={filteredInitialValues}
                schema={filterSchemas.filter}
                title={texts.listTitle}
              />
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            {texts.listTitle && (
              <Typography variant="h5">{texts.listTitle}</Typography>
            )}
          </CardContent>
        </Card>
      )}
      {showMobileView && mobileColumns ? (
        <>
          {Object.keys(filterSchemas.quickFilter).length > 0 && (
            <Card>
              <CardContent>
                <QuickFilter
                  quickFilter={filterSchemas.quickFilter}
                  currentFilter={filter}
                  onFilterChange={onQuickFilterChange}
                />
              </CardContent>
            </Card>
          )}
          <Card>
            <Box sx={{ pl: 2, pt: 1, pr: 1 }}>
              <DataListView<Row>
                data={{ nodes, count }}
                columns={mobileColumns}
                hasShowSelect={hasShowSelect}
                deleteItems={deleteItemsRunner}
                restoreItems={restoreItemsRunner}
                refetch={() => listQueryResult.refetch()}
                onCreate={
                  createButton && onClickCreate ? onClickCreate : undefined
                }
                isLoading={listQueryResult.loading}
                error={listQueryResult.error}
                texts={texts}
                selectableRows={!!deleteMutation || !!repeatMutation}
                selectableComponent={selectableComponent}
              />
            </Box>
          </Card>
        </>
      ) : (
        <Card>
          <DataTable
            loading={listQueryResult.loading}
            download={download}
            title="Список"
            data={tableData}
            nodes={nodes}
            columns={columns}
            selectableRows={selectableRows}
            hasShowSelect={hasShowSelect}
            selectableComponent={selectableComponent}
            count={count}
            page={page}
            rowsPerPage={rowsPerPage}
            serverSide
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
            createButton={_createButton}
            deleteButton={deleteButton}
            repeatButton={repeatButton}
            refetchButton={refetchButton}
            quickFilter={filterSchemas.quickFilter}
            currentFilter={filter}
            onFilterChange={onQuickFilterChange}
          />
        </Card>
      )}
    </Stack>
  );
}
